import { useEffect, useRef, useState, type RefObject } from "react";

// small dead zone so the hints don't flicker at the very top/bottom
const SCROLL_THRESHOLD = 4;

// tracks whether a scroll container has content hidden above or below the
// viewport, so callers can show up/down scroll hints in the right spot
export default function useScrollableEdges<
  T extends HTMLElement = HTMLElement,
>() {
  const ref = useRef<T | null>(null);
  const [canScrollUp, setCanScrollUp] = useState(false);
  const [canScrollDown, setCanScrollDown] = useState(false);

  useEffect(() => {
    const element = ref.current;
    if (!element) return;

    const update = () => {
      const { scrollTop, scrollHeight, clientHeight } = element;
      setCanScrollUp(scrollTop > SCROLL_THRESHOLD);
      setCanScrollDown(
        scrollTop + clientHeight < scrollHeight - SCROLL_THRESHOLD,
      );
    };

    update();

    element.addEventListener("scroll", update, { passive: true });

    // watch the element and its content so the hints update both when the
    // viewport resizes and when items get added or removed
    const observer = new ResizeObserver(update);
    observer.observe(element);
    for (const child of element.children) {
      observer.observe(child);
    }

    return () => {
      element.removeEventListener("scroll", update);
      observer.disconnect();
    };
  }, []);

  return { ref: ref as RefObject<T>, canScrollUp, canScrollDown };
}
